import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, ToastContainer, Dropdown, Modal, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faGripVertical, faEdit, faExternalLinkAlt, faExpand, faCompress, faTimes, faSave, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { createPageInstance } from '../../../Models/Page';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ComponentService, LanguageService, PageService } from '../../../Services/Axios/Api';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ComponentEditor from './ComponentEditor';
import './ComponentEditor.css';
import { GetModelInstance } from '../../../ComponentsMap';
import LanguageSelector from '../../../Components/LanguageSelector/LanguageSelector';

const ItemType = {
    COMPONENT: 'component',
};

const DraggableComponent = ({ component, index, moveComponent, onUpdate, onRemove, onEdit, id }) => {
    const [, ref] = useDrag({
        type: ItemType.COMPONENT,
        item: { index },
    });

    const [, drop] = useDrop({
        accept: ItemType.COMPONENT,
        hover: (draggedItem) => {
            if (draggedItem.index !== index) {
                moveComponent(draggedItem.index, index);
                draggedItem.index = index;
            }
        },
    });

    return (
        <div ref={(node) => ref(drop(node))} className="mb-3">
            <Card>
                <Card.Body className="cursor-grab d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <div>
                            <FontAwesomeIcon icon={faGripVertical} className="me-3 text-muted" />
                        </div>
                        <div>
                            {/* <b>{id}</b> */}
                            <h6 className="mb-0">{component.name}</h6>
                            <small className="text-muted">Order: {component.order}</small>
                        </div>
                    </div>
                    <div>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => onEdit(id, component.name)}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>

                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => onRemove(component.id)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

const PageBuilder = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    // const [id , setId] = useState(useParams().id);
    const [pageInstance, setPageInstance] = useState(createPageInstance());
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(!id);
    const [pageData, setPageData] = useState({
        title: '',
        link: '',
        isVisible: true,
        isTitleVisible: true
    });
    const [components, setComponents] = useState([]);
    const [availableComponents, setAvailableComponents] = useState([]);
    const [editingComponent, setEditingComponent] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [hasOrderChanged, setHasOrderChanged] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const [availableLanguages, setAvailableLanguages] = useState([]);   

    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [searchParams] = useSearchParams();
    const slug = searchParams.get('slug');
    const lang = searchParams.get('lang');
    const [currentLanguage, setCurrentLanguage] = useState(lang || 'en');
    

    useEffect(() => {
        if (id) {
            loadPageData();
            loadLanguages();
        }
        loadAvailableComponents();
    }, [ slug , lang]);
    const loadLanguages = async () => {
        // setAvailableLanguages([ 
        //     { 
        //         code: 'en', 
        //         name: 'English', 
        //         nativeName: 'English',
        //         flag: '🇺🇸'
        //     },
        //     { 
        //         code: 'ar', 
        //         name: 'Arabic', 
        //         nativeName: 'العربية',
        //         flag: '🇪🇬'
        //     }
        // ]);
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
        }
    }

    const loadPageData = async () => {
        try {
            await pageInstance.load(slug , lang);
            setPageInstance(pageInstance);
            
            setPageData({
                title: pageInstance?.getTitle(),
                link: pageInstance?.getLink(),
                isVisible: pageInstance?.isPageVisible(),
                isTitleVisible: pageInstance?.isTitleVisibleFlag ? pageInstance.isTitleVisibleFlag() : true
            });
            navigate(`/bj-admin/pages/page-builder/${pageInstance.id}?slug=${pageInstance.getLink()}&lang=${lang}`);
            // setId(pageInstance.id);
            console.log('pageInstance.getComponents()', pageInstance.getComponents());
            setComponents(pageInstance.getComponents());
        } catch (error) {
            console.error('Error loading page:', error);
            toast.error('Failed to load page data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePage = async () => {
        try {
            setLoading(true);
            pageInstance.setTitle(pageData.title);
            pageInstance.setLink(pageData.link);
            pageInstance.setVisibility(pageData.isVisible);
            pageInstance.setTitleVisibility(pageData.isTitleVisible);

            const response = await pageInstance.save();
            if (response) {
                setIsCreating(false);
                toast.success('Page created successfully');
                setTimeout(() => {
                    window.location.href = `/bj-admin/pages/page-builder/${response.id}?slug=${pageData.link}&lang=${lang||localStorage.getItem('selectedLanguage')}`;
                    // loadPageData();

                }, 1000);
            } else {
                throw new Error('Failed to create page');
            }
        } catch (error) {
            console.error('Error creating page:', error);
            toast.error(error.message || 'Failed to create page');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableComponents = async () => {
        try {
            const response = await ComponentService.GetAllComponents();
            // const mappedComponents = response.map(comp => ({
            //     name: comp.name,
            //     id: comp.id,
            // }));
            var tempResponse  = [
                {
                    "id": 5,
                    "name": "Slider"
                },
                
                {
                    "id": 10,
                    "name": "RichText"
                },
                {
                    "id": 11,
                    "name": "ColSection"
                },
                {
                    "id": 12,
                    "name": "HTML"
                },
                {
                    "id": 1013,
                    "name": "CardsSection"
                },
                {
                    "id": 1014,
                    "name": "Archive"
                }
            ]
            setAvailableComponents(tempResponse);
        } catch (error) {
            console.error('Error loading available components:', error);
            toast.error('Failed to load available components');
        }
    };

    const handlePageDataChange = (field, value) => {
        setPageData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddComponent = async (component) => {
        const modelInstance = GetModelInstance(component.name);
        
        try {
            setLoading(true);
            const newComponent = {
                name: component.name,
                data: modelInstance?.getData()||{},
                order: components.length + 1,
                ComponentId: component.id,
                language: currentLanguage
            };

            console.log('Adding component:', newComponent);
            const response = await pageInstance.addComponent(newComponent);
            console.log('Component added successfully:', response);

            // Refresh components list
            // await pageInstance.loadComponents();
            // setComponents(pageInstance.getComponents());
            loadPageData();
            toast.success('Component added successfully');
        } catch (error) {
            console.error('Error adding component:', error);
            toast.error(error.message || 'Failed to add component');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateComponent = (componentId, updatedData) => {
        pageInstance.updateComponent(componentId, updatedData);
        setComponents(pageInstance.getComponents());
    };

    const handleRemoveComponent = async (componentId) => {
        console.log('componentId', componentId);
        try {
            await pageInstance.removeComponent(componentId);
            setComponents(pageInstance.getComponents());
            toast.success('Component removed successfully');
        } catch (error) {
            console.error('Error removing component:', error);
            toast.error('Failed to remove component');
        }
    };

    const moveComponent = (fromIndex, toIndex) => {
        const updatedComponents = Array.from(components);
        const [movedComponent] = updatedComponents.splice(fromIndex, 1);
        updatedComponents.splice(toIndex, 0, movedComponent);
        setComponents(updatedComponents);
        pageInstance.setComponents(updatedComponents);
        setHasOrderChanged(true);
    };

    const handleSaveOrder = async () => {
        try {
            setSavingOrder(true);
            await pageInstance.saveComponentOrder();
            setHasOrderChanged(false);
            toast.success('Component order saved successfully');
        } catch (error) {
            console.error('Error saving component order:', error);
            toast.error('Failed to save component order');
        } finally {
            setSavingOrder(false);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            pageInstance.setTitle(pageData.title);
            pageInstance.setLink(pageData.link);
            pageInstance.setVisibility(pageData.isVisible);
            pageInstance.setTitleVisibility(pageData.isTitleVisible);
            const response = await pageInstance.save();
            console.log("response", response);
            if (response) {
                toast.success('Page saved successfully');
                setTimeout(() => {
                    navigate(`/bj-admin/pages/page-builder/${id}?slug=${pageData.link}&lang=${lang}`);
                }, 1000);
            } else {
                throw new Error('Failed to save page');
            }
        } catch (error) {
            console.error('Error saving page:', error);
            toast.error(error.message || 'Failed to save page');
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = () => {
        toast.info('Preview functionality will be available soon');
    };
    const handleDelete = async () => {
        try {
            await pageInstance.delete();
            toast.success('Page deleted successfully');
            setTimeout(() => {
                navigate('/bj-admin/pages');
            }, 1000);
        } catch (error) {
            console.error('Error deleting page:', error);
            toast.error('Failed to delete page');
        }
    };

    const handleEditComponent = (id, name) => {
        console.log('Editing component:', id, name);
        setEditingComponent({ id, name });
    };

    const handleCloseEditor = () => {
        setEditingComponent(null);
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        
        window.location.href = `/bj-admin/pages/page-builder/${id}?slug=${slug}&lang=${newLanguage}`;

    };

    const handleCopyFromLanguage = async (languageCode) => {
        
        try {
            const response = await PageService.CopyPageFromLanguage(slug, languageCode, currentLanguage );
            toast.success(response.result);
            setTimeout(() => {
                // navigate(`/bj-admin/pages/page-builder/${id}?slug=${slug}&lang=${currentLanguage}`);
                loadPageData();
            }, 1000);
        } catch (error) {
            console.error('Error copying from language:', error);
            toast.error('Failed to copy from language');
        }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <>
        <ToastContainer />
        <DndProvider backend={HTML5Backend}>
            <Container fluid className="p-4">
                {!isCreating && (
                    <Card className="mb-4 language-management-card">
                        <Card.Header className="language-management-header">
                            <Row className="">
                                <Col md={6}>
                                    <div className="d-flex align-items-center gap-3">
                                        <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                        <div>
                                            <h6 className="mb-0">Language Management</h6>
                                            <small className="text-muted">
                                                Managing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6} className="d-flex justify-content-end">
                                    <LanguageSelector
                                        currentLanguage={currentLanguage}
                                        availableLanguages={availableLanguages}
                                        onLanguageChange={handleLanguageChange}
                                        showBadge={false}
                                        showCopyFromLanguage={true}
                                        onCopyFromLanguage={handleCopyFromLanguage}
                                    />
                                </Col>
                            </Row>
                        </Card.Header>
                        
                        {/* Language Alert */}
                        {showLanguageAlert && (
                            <Card.Body className="p-3 language-alert-container">
                                <Alert
                                    variant={languageAlertType} 
                                    dismissible 
                                    onClose={() => setShowLanguageAlert(false)}
                                    className="mb-0"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <FontAwesomeIcon icon={faGlobe} />
                                        <div>
                                            {languageAlertType === 'warning' ? (
                                                <>
                                                    <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                                    {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                                </>
                                            ) : (
                                                <>
                                                    <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this page.
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </Alert>
                            </Card.Body>
                        )}
                    </Card>
                )}
                <Row>
                    <Col lg={12}>
                        <Card className="mb-4">
                            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                                <h4 className="mb-2">Page Settings</h4>
                                {!isCreating && (
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => window.open(`/${pageInstance.getLink()}`, '_blank')}
                                    >
                                        <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                                        Preview Page
                                    </Button>
                                )}
                            </Card.Header>
                            <Card.Body>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Page Title</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={pageData.title}
                                            onChange={(e) => handlePageDataChange('title', e.target.value)}
                                            placeholder="Enter page title"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Page Slug</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={pageData.link}
                                            onChange={(e) => {
                                                // Let the user type freely
                                                handlePageDataChange('link', e.target.value);
                                            }}
                                            onBlur={(e) => {
                                                let input = e.target.value.toLowerCase();
                                                input = input.replace(/[^a-z0-9-]/g, '-');
                                                input = input.replace(/-+/g, '-');
                                                input = input.replace(/^-|-$/g, '');
                                                handlePageDataChange('link', input);
                                            }}
                                            placeholder="Enter page link (e.g., my-page-title)"
                                            pattern="[a-z0-9\-]+"
                                            title="Only lowercase letters, numbers, and hyphens are allowed"
                                        />


                                        <Form.Text className="text-muted">
                                            Use only lowercase letters, numbers, and hyphens. Spaces and special characters will be converted to hyphens.
                                        </Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="switch"
                                            id="visibility-switch"
                                            label="Page Visibility"
                                            checked={pageData.isVisible}
                                            onChange={(e) => handlePageDataChange('isVisible', e.target.checked)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="switch"
                                            id="title-visibility-switch"
                                            label="Show Page Title"
                                            checked={pageData.isTitleVisible}
                                            onChange={(e) => handlePageDataChange('isTitleVisible', e.target.checked)}
                                        />
                                    </Form.Group>
                                </Form>
                            </Card.Body>
                        </Card>

                        {!isCreating && (
                            <Card>
                                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
                                    <div className="d-flex align-items-center gap-3">
                                        <h4 className="mb-2">Page Components</h4>
                                        {hasOrderChanged && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={handleSaveOrder}
                                                disabled={savingOrder}
                                            >
                                                {savingOrder ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                        Saving Order...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon icon={faSave} className="me-2" />
                                                        Save Order
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                    <Dropdown>
                                        <Dropdown.Toggle variant="primary" size="sm" id="dropdown-add-component">
                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                            Add Component
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                            {availableComponents.map((component) => (
                                                <Dropdown.Item
                                                    key={component.id}
                                                    onClick={() => handleAddComponent(component)}
                                                >
                                                    {component.name}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Card.Header>
                                <Card.Body>
                                    {components.map((component, index) => (
                                        <DraggableComponent
                                            key={component.id}
                                            component={component}
                                            index={index}
                                            id={component.componentId}
                                            moveComponent={moveComponent}
                                            onUpdate={handleUpdateComponent}
                                            onRemove={handleRemoveComponent}
                                            onEdit={handleEditComponent}
                                        />
                                    ))}
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
                <div className="d-flex justify-content-end mt-4">
                    {isCreating ? (
                        <Button
                            variant="primary"
                            onClick={handleCreatePage}
                            disabled={loading || !pageData.title || !pageData.link}
                        >
                            {loading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Creating...
                                </>
                            ) : (
                                'Create Page'
                            )}
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </Button>
                            <Button
                                className='ms-2'
                                variant="danger"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={loading}
                            >
                                Delete Page
                            </Button>
                        </>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete this page? This action cannot be undone and will delete all languages of this page.</p>
                        <p className="text-danger mb-0">Page Title: <strong>{pageData.title}</strong></p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={() => {
                                setShowDeleteModal(false);
                                handleDelete();
                            }}
                        >
                            Delete Page
                        </Button>
                    </Modal.Footer>
                </Modal>

                {editingComponent && (
                    <ComponentEditor
                        component={editingComponent}
                        onClose={handleCloseEditor}
                    />
                )}
            </Container>
        </DndProvider>
        </>
    );
};

export default PageBuilder;